/**
 * Wiring: auth -> connection -> fleet model -> DOM.
 *
 * The whole app is: sign in, get a token, connect with it, render what arrives. Every
 * access decision belongs to the broker; this file never filters.
 */
import { accessToken, aclFromToken, clearCallbackParams, currentUser, getAccessToken,
         sitesFromToken,
         handleRedirect, isLoggedIn, login, logout, redirectError, subject,
         subscribeFiltersFromToken } from "./auth";
import { Connection } from "./connection";
import { Fleet, Robot, applyMessage, keyOf, parseTopic } from "./fleet";
import { robotColour } from "./robotColour";
import { readAreas } from "./mapPoints";
import { applyDebugMessage, cachedAnswer, emptyDebugState, objectsWith, refreshQuery,
         robotParam, sendQuery, sendRobotQuery,
         type DebugState } from "./debugQuery";
import { diagnose, diagnoseBroker, diagnoseRedirect, diagnoseToken } from "./diagnose";
import { HOME, formatHash, parseHash, storeIdFor, type Nav } from "./nav";
import { ARM_TIMEOUT_MS, COMMANDS_ENABLED, actionViews, coerceArgs, disarmed, isArmed,
         isConfirming, newCommandId, press, type CommandAttempt } from "./commands";
import { robotTopic } from "./contract/fleetComms";
import { clearConnError, clearLoginError, renderDebugPanel, renderDetail,
         renderActions, renderGuided, renderMap, renderMockPanel, resetDetailRenderState,
         setMapRepaint, 
         setStatus, showScreens,
         showApp, showConnError, showDiagnostics, showLogin, showLoginError,
         showNoAccess } from "./ui";
import { cfg } from "./config";
import { fetchState, isUp, mockConfigured, setLifecycle, type Verb } from "./mockControl";

let fleet: Fleet = new Map();
let conn: Connection | null = null;
let dirty = false;

// The vendor debug surface. Separate from `fleet` because it is not robot state: a
// manifest describes the ADAPTER, and a result answers a question somebody asked.
const dbg: DebugState = emptyDebugState();
let dbgSel: { siteId: string; call: string; values: Record<string, string> } | null = null;
let dbgError = "";

// The drill-down. Null means the fleet table. Held as ids rather than a Robot, so a row
// replaced by a fresh fleet listing does not leave this view pinned to a stale object.
let detail: { siteId: string; robotId: string } | null = null;
let detailCall: string | null = null;
let detailValues: Record<string, string> = {};
let detailError = "";
let guidedError = "";
/**
 * Every site this account may see, from the token's ACL claim.
 *
 * FROM THE TOKEN, not from observed traffic. A site appears here whether or not its
 * adapter has published anything — which is the whole point, because the quiet site is
 * the one somebody opened this screen to look at. Deriving it from the roster instead
 * showed only whichever site happened to be first alphabetically.
 */
let grantedSites: string[] = [];
/**
 * Arming state for the command surface. Per visit and in memory — NOT persisted, and
 * reset whenever a different robot is opened. Carrying an armed state from one robot to
 * another is precisely the mistake the arming exists to prevent.
 */
let armState = disarmed();
/**
 * What this panel tried to issue, newest first. Bounded and per visit.
 *
 * Kept when LIVE as well as in the harness: `telemetry/command` tells you a command was
 * rejected, and only this tells you which arguments were in it. A refusal whose request
 * you cannot see is a refusal you cannot act on.
 */
let attempts: CommandAttempt[] = [];
/** Typed arguments per operation, for the parameterised commands. Cleared with the
 *  arming when a different robot is opened. */
let actionValues: Record<string, Record<string, string>> = {};
/** Which operation's form is on screen. Reset with the robot, like everything else here
 *  — arriving at a robot with somebody else's half-filled delete form would be its own
 *  small hazard. */
let actionSelected = "";

// ---- navigation --------------------------------------------------------------
//
// The hash is the state. `go()` writes it and lets `hashchange` do the rendering, so
// there is ONE path into a screen whether it was reached by a click, the back button, or
// a pasted link — a second path is how a view drifts from its URL.

let nav: Nav = HOME;

function go(next: Nav): void {
  const h = formatHash(next);
  if (location.hash === h) { applyNav(); return; }   // same screen: repaint, no entry
  location.hash = h;                                  // fires hashchange -> applyNav
}

function applyNav(): void {
  nav = parseHash(location.hash);
  if (nav.view === "robot" && nav.robotId && nav.siteId) {
    openRobot(nav.siteId, nav.robotId);
    return;
  }
  detail = null;
  showScreens({ guided: true });
  paintGuided();
  // The vendor-query panel travels WITH the site screen now rather than being a
  // destination. It is the only way to invoke an arbitrary registry call, so retiring
  // the flat table it used to sit beside must not retire it too.
  paintDebug();
}

/** Selected scene and floor on the site workspace. Per visit and in memory: reopening a
 *  site should not silently spend a map_fetch on a floor somebody chose yesterday. */
let scene: string | null = null;
let floor: string | null = null;
/** Robots whose location is being polled. Also per visit, and for a stronger reason:
 *  polling costs vendor calls, so it must never outlive the screen that asked for it. */
const polled = new Set<string>();

/**
 * How often a ticked robot is polled.
 *
 * ONE CYCLE IS SEVERAL VENDOR CALLS (status, battery, location), and this vendor answers
 * over-polling by DISABLING THE CLIENT_ID rather than throttling. 15 s matches the
 * adapter's own default interval, so a panel with one robot ticked costs no more than
 * the adapter would if polling were enabled there.
 */
const POLL_SECONDS = 15;

function paintGuided(): void {
  renderGuided(nav, fleet, dbg, grantedSites, Date.now(), {
    onGo: go,
    onLoadScenes: () => {
      if (!conn || !nav.siteId) return;
      guidedError = runSiteCall("scene_list", { storeId: storeIdFor(nav.siteId) });
      paintGuided();
    },
    scene,
    floor,
    onScene: (code) => {
      scene = code;
      // A new scene invalidates the floor: floor ids come from the scene's own points,
      // and carrying one across would ask for a floor that may not exist here.
      floor = null;
      if (code) {
        // Both, together: targets are what carry the floor ids, and the points are what
        // the map is drawn with. Asking for one and not the other leaves a dropdown that
        // never populates for reasons nobody can see.
        guidedError = runSiteCall("scene_targets", { sceneCode: code })
                   || runSiteCall("map_points_v2", { sceneCode: code, floorInfo: "1" });
      }
      paintGuided();
    },
    onFloor: (f) => {
      floor = f;
      // THE MAP IS FETCHED HERE AND ONLY HERE. Choosing a floor is the deliberate act
      // that spends a map_fetch; opening a site is not.
      if (f && scene) guidedError = runSiteCall("map_fetch", { sceneCode: scene, floorInfo: f });
      paintGuided();
    },
    onLoadAreas: () => {
      // `storeId` only. The call's scope is `robot` because its optional `robotSn`
      // carries a robot-picker hint, but only `storeId` is required — so it answers for
      // the whole site in one call rather than one per robot.
      guidedError = runSiteCall("area_list", { storeId: storeIdFor(nav.siteId ?? "") });
      paintGuided();
    },
    polled,
    onPolled: (robotId, on) => {
      if (on) {
        polled.add(robotId);
        pollRobot(robotId);      // once immediately, so ticking the box does something
      } else {
        polled.delete(robotId);
      }
      paintGuided();
    },
    pollSeconds: POLL_SECONDS,
    error: guidedError,
  });

  // The map belongs to the site screen, under the dropdowns and above the robots. Shown
  // only once a floor is chosen, and carrying the polled robots' positions.
  const showMap = nav.view === "robots" && !!scene && !!floor;
  renderMap(showMap ? siteStub(nav) : undefined, dbg, showMap, "site",
            polledPositions(nav.siteId ?? ""), floor);
}

/** One scene- or site-scoped vendor call. Returns an error string, or "" for sent. */
function runSiteCall(name: string, values: Record<string, string>): string {
  if (!conn || !nav.siteId) return "not connected";
  const call = dbg.manifests.get(nav.siteId)?.calls.find((c) => c.name === name);
  if (!call) return `no adapter on ${nav.siteId} offers ${name}`;
  return sendQuery(conn, dbg, nav.siteId, call, values).error ?? "";
}

/** The ticked robots that actually have a pose, with their colours. */
function polledPositions(siteId: string) {
  const out: Array<{ id: string; colour: string;
                     pos: NonNullable<Robot["position"]> }> = [];
  for (const id of polled) {
    const r = fleet.get(keyOf(siteId, id));
    // A robot with no pose yet is simply not drawn. Drawing it at the origin would put
    // it somewhere real-looking and wrong, which is worse than an absent dot.
    if (r?.position) out.push({ id, colour: robotColour(id), pos: r.position });
  }
  return out;
}

/** Ask the adapter for one poll cycle on this robot. Its answer arrives as telemetry,
 *  not as a reply — which is why nothing here waits for one. */
function pollRobot(robotId: string): void {
  // The site comes from the NAV, which carries it on every robot screen — the list and
  // the drill-down alike.
  const siteId = nav.siteId ?? detail?.siteId;
  if (!conn || !siteId) return;
  const err = conn.publish(robotTopic(siteId, robotId, "cmdPoll"), {}, "cmdPoll");
  if (err) guidedError = err;
}

// The poll loop. Runs while robots are ticked AND a robot screen is on — the site list
// or one robot's own view.
//
// IT USED TO STOP ON THE ROBOT VIEW, which was backwards and cost a live test: drilling
// into a robot is MORE focused on it, not less, and the state simply froze the moment
// somebody opened the screen they were about to command from. The guard's real purpose
// is to stop spending vendor calls when nobody is looking at robots at all — a forgotten
// timer is how a client_id gets disabled overnight — and the site LIST is not the only
// screen where somebody is looking.
setInterval(() => {
  if (nav.view === "sites" || !polled.size) return;
  for (const id of polled) pollRobot(id);
}, POLL_SECONDS * 1000);

/**
 * A stand-in carrying just the site, so the map renderer can find that site's cached
 * answers.
 *
 * Deliberately NOT one of the real robots: the site map draws every polled robot as a
 * coloured dot from `others`, and handing the renderer one of them would single it out
 * as "the" robot — drawn differently, and implying a relationship to the map that it has
 * no more than any of the others.
 */
function siteStub(n: Nav) {
  return { siteId: n.siteId ?? "", robotId: "", lastSeen: 0 };
}

function openRobot(siteId: string, robotId: string) {
  // A different robot's feed and log must not be mistaken for this one's while the
  // change-detection catches up.
  resetDetailRenderState();
  detail = { siteId, robotId };
  armState = disarmed();          // never arrive at a robot with the controls live
  attempts = [];                  // and never carry one robot's attempts onto another
  actionValues = {};
  actionSelected = "";
  detailCall = null;
  detailValues = {};
  detailError = "";
  showScreens({ detail: true });
  paintDetail();
}

function closeRobot() {
  // Back to wherever this robot was opened FROM. Arriving via the guided flow and being
  // returned to the flat table would be the app forgetting where you were.
  go(nav.view === "robot" && nav.siteId
     ? { source: nav.source, view: "robots", siteId: nav.siteId }
     : HOME);
}

function paintDetail() {
  if (!detail) return;
  renderDetail(
    fleet.get(keyOf(detail.siteId, detail.robotId)), dbg, detailCall, detailValues,
    Date.now(),
    {
      error: detailError,
      onBack: closeRobot,
      onSelect: (call) => {
        // Values reset per call: carrying a leftover parameter into a different call is
        // how a query gets refused for something the operator cannot see.
        detailCall = call;
        detailValues = {};
        detailError = "";
        paintDetail();
      },
      onField: (name, value) => { detailValues[name] = value; },
      onSend: () => {
        if (!conn || !detail || !detailCall) return;
        const m = dbg.manifests.get(detail.siteId);
        const call = m?.calls.find((c) => c.name === detailCall);
        if (!call) return;
        const out = sendRobotQuery(conn, dbg, detail.siteId, detail.robotId, call,
                                   detailValues);
        detailError = out.error ?? "";
        paintDetail();
      },
      onRefresh: (name) => {
        if (!conn || !detail) return;
        const call = dbg.manifests.get(detail.siteId)?.calls.find((c) => c.name === name);
        if (!call) return;
        const out = refreshQuery(conn, dbg, detail.siteId, call, detail.robotId);
        detailError = out.error ?? "";
        paintDetail();
      },
    });

  // Commands and the spatial view are V2 ONLY. The classic view has never had an action
  // surface, and the first place a robot can be moved from should be the one under
  // review rather than the one people already use out of habit.
  const v2 = nav.view === "robot";
  const robot = fleet.get(keyOf(detail.siteId, detail.robotId));
  const now = Date.now();
  renderActions(robot, {
    armed: isArmed(armState, now),
    confirming: armState.pending && isConfirming(armState, armState.pending.name, now)
      ? armState.pending.name : null,
    armSecondsLeft: isArmed(armState, now)
      ? Math.max(0, Math.ceil((armState.armedAt + ARM_TIMEOUT_MS - now) / 1000))
      : undefined,
    // The same poll toggle the site list carries. This is the screen somebody commands
    // from and therefore the screen where live state matters most; having to go back a
    // level to turn it on was the wrong way round.
    polled: polled.has(detail.robotId),
    onPolled: (on) => {
      if (on) { polled.add(detail!.robotId); pollRobot(detail!.robotId); }
      else polled.delete(detail!.robotId);
      paintDetail();
    },
    pollSeconds: POLL_SECONDS,
    attempts,
    values: actionValues,
    selected: actionSelected,
    onSelectCommand: (name) => { actionSelected = name; paintDetail(); },
    // Re-fetch the list behind a picker. All four are already in the read registry and
    // already cached, so this is a deliberate refresh rather than a routine cost.
    onRefreshOptions: (hint) => {
      if (!detail) return;
      const call = {
        "area-picker": "area_list",
        "clean-model-picker": "clean_models",
        "back-point-picker": "back_points",
        "schedule-picker": "strategy_list",
      }[hint];
      if (!call) return;
      const entry = dbg.manifests.get(detail.siteId)?.calls.find((c) => c.name === call);
      if (!conn || !entry) {
        detailError = entry ? "not connected" : `no adapter offers ${call}`;
        paintDetail();
        return;
      }
      // Sent FRESH rather than through `refreshQuery`, which reuses a previous answer's
      // parameters — and these lists may never have been asked for from this view, in
      // which case there is no previous answer to reuse and the button would report
      // "nothing to refresh" instead of fetching anything.
      //
      // `storeId` is supplied for the calls that want it; `sendRobotQuery` fills the
      // robot in for the rest, which is the whole reason a robot-scoped call never asks
      // an operator to retype a serial.
      const values: Record<string, string> = {};
      if (entry.params?.storeId) values.storeId = storeIdFor(detail.siteId);
      const out = robotParam(entry)
        ? sendRobotQuery(conn, dbg, detail.siteId, detail.robotId, entry, values, true)
        : sendQuery(conn, dbg, detail.siteId, entry, values, undefined, true);
      detailError = out.error ?? "";
      paintDetail();
    },
    onField: (action, field, value) => {
      actionValues[action] = { ...(actionValues[action] ?? {}), [field]: value };
    },
    // EVERY ARGUMENT IS PICKED, NOT TYPED. All three come from calls already in the read
    // registry and already cached, which is the prerequisite chain paying off: an
    // operator composing a cleaning run never has to know a vendor uuid.
    pick: (hint) => {
      if (!detail) return [];
      const site = detail.siteId;
      if (hint === "area-picker") {
        return readAreas(cachedAnswer(dbg, site, "area_list")?.data)
          .map((a) => ({ value: a.id, label: a.name }));
      }
      if (hint === "clean-model-picker") {
        return objectsWith(cachedAnswer(dbg, site, "clean_models")?.data, "cleanModelId")
          .map((o) => ({ value: String(o.cleanModelId),
                         label: String(o.cleanModelName ?? o.cleanModelId) }));
      }
      if (hint === "schedule-picker") {
        // From `strategy_list`, whose `id` IS the strategyId. The vendor gives no
        // get-by-id, so this list is the only route from a schedule to its identifier —
        // including for schedules created on the robot's own screen.
        return objectsWith(cachedAnswer(dbg, site, "strategy_list")?.data, "id")
          .map((o) => ({ value: String(o.id),
                         label: String(o.cleanName ?? o.id) }));
      }
      if (hint === "back-point-picker") {
        return objectsWith(cachedAnswer(dbg, site, "back_points")?.data, "backPointId")
          .map((o) => ({ value: String(o.backPointId),
                         label: String(o.backPointName ?? o.backPointId) }));
      }
      return [];
    },
    onArm: (on) => { armState = on ? { armedAt: Date.now() } : disarmed(); paintDetail(); },
    onPress: (name) => {
      if (!detail) return;
      const r = press(armState, name, Date.now());
      armState = r.next;
      if (r.send) {
        const commandId = newCommandId();
        const topic = robotTopic(detail.siteId, detail.robotId, "cmdAction");
        // The outcome arrives on `telemetry/command`, correlated by this id — a
        // high-level operation can be REFUSED, and a refusal is indistinguishable from
        // "hasn't happened yet" by watching telemetry. Nothing here waits for a reply.
        const view = actionViews(robot).find((v) => v.name === name);
        // TYPED AGAINST THE OPERATION'S OWN SCHEMA. A form gives strings; the adapter
        // validates against this same schema and refuses `"6"` where an integer is
        // declared — which is exactly how the first live `clean` was rejected, by us,
        // before it reached the vendor.
        const args = view ? coerceArgs(view, actionValues[name] ?? {}) : {};
        // Sent only when the operation declares parameters — an operation that takes
        // none must be sent none, and the adapter refuses an extra rather than ignoring
        // it, because on an actuation surface the thing that looked accepted moves a
        // machine.
        const payload = Object.keys(args).length
          ? { name, command_id: commandId, params: args }
          : { name, command_id: commandId };
        // RECORDED EITHER WAY, before anything else. What was asked for is the half
        // `telemetry/command` cannot tell you: it reports that a command was rejected,
        // and only this reports which arguments were in it.
        const wouldRefuse = !COMMANDS_ENABLED && view && view.state !== "ready"
          ? view.reason : "";
        if (COMMANDS_ENABLED) {
          detailError = conn ? (conn.publish(topic, payload, "cmdAction") ?? "")
                             : "not connected to the broker";
        } else {
          // THE HARNESS: everything above ran — both gates, the permitted check, the id
          // — and the publish is the only step skipped.
          detailError = "";
        }
        attempts = [{ at: Date.now(), name, topic, payload, wouldRefuse,
                      // A publish that failed is NOT a command that was sent. Saying
                      // otherwise would leave an operator waiting for an outcome that
                      // has no request behind it.
                      sent: COMMANDS_ENABLED && !detailError },
                    ...attempts].slice(0, 10);
      }
      paintDetail();
    },
  }, v2);
  renderMap(robot, dbg, v2);
}

/** Robot ids known for one site, from the retained `fleet/robots` roster plus anything
 *  that has since spoken on its own telemetry topics. Sorted so the list is stable —
 *  a picker whose order changes under the cursor is its own small betrayal. */
function rosterFor(siteId: string): string[] {
  return [...fleet.values()]
    .filter((r) => r.siteId === siteId)
    .map((r) => r.robotId)
    .sort();
}

function paintDebug() {
  // The retained roster is where robot-picker options come from — it arrives free on
  // subscribe, so offering a choice of robots costs no vendor call at all.
  renderDebugPanel(dbg, dbgSel, rosterFor, {
    error: dbgError,
    onSelect: (siteId, call) => {
      // Values are per-selection, so switching calls does not carry a robotSn into a
      // store-scoped query where it would be rejected as an unexpected parameter.
      dbgSel = { siteId, call, values: {} };
      dbgError = "";
      paintDebug();
    },
    onField: (name, value) => {
      if (dbgSel) dbgSel.values[name] = value;
    },
    onSend: () => {
      if (!conn || !dbgSel) return;
      const m = dbg.manifests.get(dbgSel.siteId);
      const call = m?.calls.find((c) => c.name === dbgSel!.call);
      if (!call) return;
      const out = sendQuery(conn, dbg, dbgSel.siteId, call, dbgSel.values);
      dbgError = out.error ?? "";
      paintDebug();
    },
    onRefresh: (name) => {
      if (!conn || !dbgSel) return;
      const call = dbg.manifests.get(dbgSel.siteId)?.calls.find((c) => c.name === name);
      if (!call) return;
      const out = refreshQuery(conn, dbg, dbgSel.siteId, call);
      dbgError = out.error ?? "";
      paintDebug();
    },
  });
}

function scheduleRender() {
  // Coalesce: a poll cycle delivers a burst, and re-rendering per message would thrash
  // the DOM for no visible benefit.
  dirty = true;
}

setInterval(() => {
  // Also re-renders on a timer even when nothing arrives, so the "age" column keeps
  // counting and a robot going quiet becomes visible rather than frozen.
  if (dirty || fleet.size) {
    // One view at a time, and the guided screens are one of them now — otherwise a
    // robot going offline while somebody is looking at the store's robot list would
    // simply not update, which is the failure this tick exists to prevent.
    //
    // The detail view re-reads the raw ring here rather than on every push, which is
    // what keeps the highest-rate channel off the render path.
    if (detail) paintDetail();
    else paintGuided();
    dirty = false;
  }
}, 1000);

// ONE path into a screen, whether it came from a click, the back button, or a pasted
// link. Rendering from `hashchange` rather than from each click is what keeps the URL
// and the view from drifting apart.
// Changing the map scale must redraw at once rather than waiting for the tick, or the
// number you typed and the map you are looking at disagree for up to a second.
setMapRepaint(() => { if (detail) paintDetail(); else paintGuided(); });

window.addEventListener("hashchange", applyNav);
applyNav();

async function startSession() {
  const user = await currentUser();
  showApp(user?.email ?? user?.name ?? "signed in");

  // Purely informational: with no grants the screen would otherwise be empty and
  // indistinguishable from "nothing has happened yet".
  const tok = await accessToken();
  showNoAccess(!!tok && (aclFromToken(tok)?.length ?? 0) === 0);

  // The guided flow's root. Taken here, from the token, so the site list exists before
  // the broker connection does and a site with nothing to say is still on it.
  grantedSites = tok ? sitesFromToken(tok) : [];
  paintGuided();

  // Harness panel, if this build carries a mock URL and key. Deliberately NOT polled on
  // a timer: the mock is a stopped machine most of the time, and a background poll would
  // both keep waking it and cost a request every tick for a panel nobody is watching.
  void refreshMock();

  conn = new Connection({
    onState: (s, d) => {
      setStatus(s, d);
      if (s === "connected") clearConnError();
      if (s === "rejected") {
        // Auth0 is fine; the broker said no. Stay signed in and name the right system.
        conn?.stop();
        const b = diagnoseBroker(d ?? "");
        showConnError(b.summary, b.fix);
      }
      if (s === "unauthorized") {
        // Terminal for the CONNECTION — stop retrying, since nothing will improve
        // without a token. But do NOT sign the user out: they authenticated perfectly
        // well, and it is the BROKER leg that failed. Signing out here discarded a valid
        // session, bounced straight back to the login screen, and destroyed the only
        // evidence of what went wrong.
        conn?.stop();
        const t = diagnoseToken(d ?? "");
        showConnError(t.summary, t.fix);
      }
    },
    onMessage: (topic, payload) => {
      fleet = applyMessage(fleet, topic, payload, Date.now());
      // The debug channels are site-level and carry no robot, so they never reach the
      // fleet model; routed here instead of being dropped by it.
      const p = parseTopic(topic);
      // `fleetDebugCache` joins the two it already handled. It is RETAINED, so a batch of
      // them arrives the moment we subscribe — which is how the guided screens have data
      // to show before anybody has asked a question.
      if (p && (p.name === "fleetDebugResult" || p.name === "fleetDebugManifest" ||
                p.name === "fleetDebugCache")) {
        try {
          // An EMPTY payload is MQTT's retained-message delete, not malformed JSON. It
          // has to reach the handler as a clear; letting JSON.parse throw here would
          // leave a swept orphan on screen for the life of the session.
          const text = new TextDecoder().decode(payload);
          const value = text.trim() === "" ? null : JSON.parse(text);
          if (applyDebugMessage(dbg, p.siteId, p.name, value, p.params.entryId)) {
            if (detail) paintDetail();
            else paintDebug();
          }
        } catch {
          // A debug payload that will not parse is the adapter's problem, not a reason
          // to take the panel down. The rest of the app keeps rendering.
        }
      }
      scheduleRender();
    },
    getToken: getAccessToken,
    getFilters: subscribeFiltersFromToken,
    getUsername: subject,
  });
  await conn.start();
}

/**
 * Refresh the harness panel.
 *
 * A mock that does not answer is the NORMAL resting state — it is stopped between
 * sessions and fly starts it on the next request — so an unreachable mock renders as
 * "stopped", not as an error. Only a call that was refused is an error worth showing.
 */
async function refreshMock(busy = false): Promise<void> {
  if (!mockConfigured()) return renderMockPanel(null, { configured: false });
  renderMockPanel(null, { configured: true, busy: true });
  if (!(await isUp())) return renderMockPanel(null, { configured: true, busy });
  try {
    renderMockPanel(await fetchState(), { configured: true });
  } catch (e) {
    renderMockPanel(null, { configured: true, error: String((e as Error).message ?? e) });
  }
}

async function mockVerb(verb: Verb): Promise<void> {
  // The first call after an idle period WAKES a stopped machine, which takes a few
  // seconds. Disabling the buttons and saying "starting…" is the difference between
  // that and an app that looks hung.
  renderMockPanel(null, { configured: true, busy: true });
  try {
    await setLifecycle("all", verb);
    await refreshMock();
  } catch (e) {
    renderMockPanel(null, { configured: true, error: String((e as Error).message ?? e) });
  }
}

async function main() {
  document.getElementById("login-btn")!.addEventListener("click", () => void login());
  document.getElementById("mock-wake")!.addEventListener("click", () => void mockVerb("wake"));
  document.getElementById("mock-sleep")!.addEventListener("click", () => void mockVerb("sleep"));
  document.getElementById("mock-refresh")!.addEventListener("click", () => void refreshMock());
  document.getElementById("logout-btn")!.addEventListener("click", () => {
    conn?.stop();
    void logout();
  });
  // Explicit re-auth, offered instead of silently signing the user out when a token
  // cannot be renewed. Their choice, and the error stays on screen until they make it.
  document.getElementById("reauth-btn")!.addEventListener("click", () => {
    conn?.stop();
    void login();
  });

  // Always visible on the login card: which tenant and app this BUILD points at. Baked
  // in at build time, so there is otherwise no way to tell a misconfigured build from a
  // misconfigured tenant.
  showDiagnostics([cfg.auth0Domain, `client ${cfg.auth0ClientId.slice(0, 8)}…`,
                   `aud ${cfg.audience}`]);

  // Auth0 refuses a login by redirecting with ?error= and NO code. Handle it first:
  // otherwise the exchange below finds nothing to do, the session is never established,
  // and the login screen simply reappears — which is what a refused Action looks like
  // from the user's side.
  const refused = redirectError();
  if (refused) {
    console.error("[fleet] Auth0 refused the login:", refused);
    clearCallbackParams();
    showLogin();
    const d = diagnoseRedirect(refused.error, refused.description);
    showLoginError(d.summary, d.fix, `${refused.error}: ${refused.description}`);
    return;
  }

  const hadCallback = window.location.search.includes("code=");
  let failure: unknown = null;
  try {
    await handleRedirect();
  } catch (e) {
    failure = e;
    // Console too: the underlying error names the Auth0 setting more precisely than any
    // mapping can, and it is what to paste into a support thread.
    console.error("[fleet] redirect callback failed:", e);
  }

  if (await isLoggedIn()) {
    clearLoginError();
    await startSession();
    return;
  }

  showLogin();
  if (failure || hadCallback) {
    // Reaching here WITH a callback in the URL means Auth0 authenticated the user and
    // the exchange still failed — the exact case that otherwise looks like a login
    // screen that ignores you.
    const d = diagnose(failure ?? new Error("session was not established"),
                       window.location.origin);
    showLoginError(d.summary, d.fix, failure ? String((failure as Error).message ?? failure) : undefined);
  }
}

void main();
